import React from "react";
import Reveal from "./Reveal";

const timeline = [
  { year: "1972", title: "Foundation", body: "P. Jasvantrai & Co. is established in Mumbai as a licensed sanitary engineering practice." },
  { year: "1985", title: "Industrial Expansion", body: "Begins long-term collaboration with leading construction houses across Maharashtra." },
  { year: "1998", title: "High-Rise Specialism", body: "Develops dedicated capability for multi-storey plumbing risers and pressurised water systems." },
  { year: "2008", title: "MIDC Mandate", body: "Executes back-to-back industrial plumbing programmes across MIDC Chakan, Ranjangaon and Talegaon." },
  { year: "2015", title: "Mivan Integration", body: "Pioneers in-house Mivan-compatible plumbing coordination and precision sleeve planning." },
  { year: "2024", title: "Second Generation", body: "Continues legacy with modern materials, BIM-aware planning and a workforce trained across disciplines." },
];

// Real on-site photography. Add more entries to surface them in the gallery.
const fieldImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_pjasvantrai-legacy/artifacts/l1zfwph0_hemal%20fua%20images%20.jpeg",
    title: "Belden India",
    meta: "MIDC Chakan, Pune · Industrial",
    alt: "Belden manufacturing facility façade — MIDC Chakan",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_pjasvantrai-legacy/artifacts/0h9rpwuz_hfi4.jpeg",
    title: "Mahindra Logistics",
    meta: "Logistics · Maharashtra",
    alt: "Mahindra Logistics warehouse façade with signage",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_pjasvantrai-legacy/artifacts/jbz05fqx_hfi3.jpeg",
    title: "OEN India Ltd.",
    meta: "Chakan, Pune · Industrial",
    alt: "OEN India Ltd. plant signage above the manufacturing facility",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_pjasvantrai-legacy/artifacts/is8z83iu_WhatsApp%20Image%202026-06-10%20at%201.11.29%20PM.jpeg",
    title: "Bonfiglioli",
    meta: "Industrial · Maharashtra",
    alt: "Bonfiglioli manufacturing plant façade",
  },
];

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxhcmNoaXRlY3QlMjBibHVlcHJpbnQlMjBkYXJrfGVufDB8fHx8MTc3ODY1ODI1MHww&ixlib=rb-4.1.0&q=85";

export const About = () => {
  return (
    <section id="about" data-testid="about-section" className="relative py-28 md:py-40 bg-bone-100">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          {/* Left: heading + image */}
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-gold-deep">
                / 01 — The House of Jasvantrai
              </span>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight">
                A practice <span className="italic text-gold-deep">measured</span> in decades, not deadlines.
              </h2>
            </Reveal>

            <Reveal delay={240} className="mt-10 blueprint-border overflow-hidden">
              <img
                src={ABOUT_IMG}
                alt="Engineering blueprint"
                className="w-full h-72 md:h-96 object-cover grayscale-[0.4]"
                loading="lazy"
              />
            </Reveal>
          </div>

          {/* Right: prose + timeline */}
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <p className="text-lg md:text-xl text-slate-800 leading-[1.75] max-w-2xl">
                For over five decades, P. Jasvantrai &amp; Co. has been entrusted with the
                quiet engineering that keeps modern buildings running — the plumbing,
                drainage and water-supply systems that sit unseen behind every successful
                project.
              </p>
            </Reveal>
            <Reveal delay={100}>
              <p className="mt-7 text-lg md:text-xl text-slate-800 leading-[1.75] max-w-2xl">
                Our workmanship is rooted in precision, our planning in patience, and our
                relationships in the kind of trust that only consistent delivery, project
                after project, can build. We have evolved alongside modern construction —
                most notably the Mivan formwork system — while preserving the discipline of
                a traditional engineering house.
              </p>
            </Reveal>

            <div className="mt-14 relative">
              {/* timeline rail */}
              <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-900/15" />
              <ul className="space-y-10">
                {timeline.map((t, i) => (
                  <Reveal as="li" delay={i * 80} key={t.year} className="relative pl-12">
                    <span className="absolute left-[3px] top-2 h-[14px] w-[14px] rounded-full bg-gold/20 ring-1 ring-gold/70 flex items-center justify-center">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                    </span>
                    <div className="flex items-baseline gap-5">
                      <span className="font-display text-3xl md:text-4xl text-slate-900 tracking-tight">
                        {t.year}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold-deep">
                        {t.title}
                      </span>
                    </div>
                    <p className="mt-2 text-base md:text-lg text-slate-700 max-w-xl leading-[1.7]">
                      {t.body}
                    </p>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* From the Field — real on-site photography */}
        {fieldImages.length > 0 && (
          <div className="mt-28 md:mt-36" data-testid="field-gallery">
            <div className="grid grid-cols-12 gap-6 items-end mb-10">
              <div className="col-span-12 lg:col-span-7">
                <Reveal>
                  <span className="font-mono text-xs uppercase tracking-[0.34em] text-gold-deep">
                    From the Field
                  </span>
                </Reveal>
                <Reveal delay={120}>
                  <h3 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl text-slate-900 leading-[1.02] tracking-tight">
                    Photographs from <span className="italic text-gold-deep">live sites</span>.
                  </h3>
                </Reveal>
              </div>
              <div className="col-span-12 lg:col-span-5">
                <Reveal delay={200}>
                  <p className="text-base md:text-lg text-slate-700 leading-[1.7]">
                    Documentary images captured during execution — a quiet record of the
                    facilities, partners and infrastructure we have helped commission.
                  </p>
                </Reveal>
              </div>
            </div>

            <div
              className={(() => {
                const n = fieldImages.length;
                if (n === 1) return "grid grid-cols-1 lg:grid-cols-5 gap-px bg-slate-900/8 border border-slate-900/8";
                if (n === 2) return "grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-900/8 border border-slate-900/8";
                if (n === 4) return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-px bg-slate-900/8 border border-slate-900/8";
                return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-900/8 border border-slate-900/8";
              })()}
            >
              {fieldImages.map((img, i) => (
                <Reveal
                  key={img.src}
                  delay={i * 80}
                  className={`group relative bg-ink-800 overflow-hidden ${
                    fieldImages.length === 1 ? "lg:col-span-3" : ""
                  }`}
                  data-testid={`field-image-${i}`}
                >
                  <div
                    className={`relative overflow-hidden ${
                      fieldImages.length === 1
                        ? "aspect-[16/10]"
                        : fieldImages.length === 4
                        ? "aspect-[4/3]"
                        : "aspect-[4/5]"
                    }`}
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      className="w-full h-full object-cover grayscale-[0.15] transition-all duration-[1200ms] ease-out group-hover:scale-[1.04] group-hover:grayscale-0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/30 to-transparent opacity-90" />
                  </div>
                  <div className="absolute left-6 right-6 bottom-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                      Plate · {String(i + 1).padStart(2, "0")}
                    </div>
                    <h4 className="mt-3 font-display text-2xl md:text-[26px] text-white leading-tight">
                      {img.title}
                    </h4>
                    <div className="mt-2 text-xs md:text-sm text-steel-light">
                      {img.meta}
                    </div>
                  </div>
                  <div className="absolute left-0 bottom-0 h-px bg-gold w-0 group-hover:w-full transition-all duration-700" />
                </Reveal>
              ))}

              {/* Editorial caption block — only when exactly one image */}
              {fieldImages.length === 1 && (
                <Reveal
                  delay={160}
                  className="lg:col-span-2 bg-ink-800 p-8 md:p-10 flex flex-col justify-between"
                >
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                      Selected Plate
                    </div>
                    <h4 className="mt-5 font-display text-3xl md:text-4xl text-white leading-[1.1]">
                      A live record of <span className="italic text-gold">work in progress</span>.
                    </h4>
                    <p className="mt-5 text-sm md:text-base text-steel-light leading-relaxed">
                      Captured during execution at the Belden facility — one of several
                      industrial commissions currently underway in association with our
                      construction partners across the Pune corridor.
                    </p>
                  </div>
                  <div className="mt-8 pt-8 border-t border-white/5 grid grid-cols-2 gap-6">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-steel-light">Status</div>
                      <div className="mt-2 font-display text-xl text-white">Ongoing</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-steel-light">Sector</div>
                      <div className="mt-2 font-display text-xl text-white">Industrial</div>
                    </div>
                  </div>
                </Reveal>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default About;
